import {
  useSyncExternalStore,
  type AnchorHTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from 'react';

const listeners = new Set<() => void>();

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  window.addEventListener('popstate', cb);
  return () => {
    listeners.delete(cb);
    window.removeEventListener('popstate', cb);
  };
}

const getPath = (): string => window.location.pathname.replace(/\/+$/, '') || '/';

/** Current pathname, re-rendering on navigation. */
export function usePath(): string {
  return useSyncExternalStore(subscribe, getPath, () => '/');
}

const getSearch = (): string => window.location.search;

/** Current query string as URLSearchParams, re-rendering on navigation. */
export function useSearch(): URLSearchParams {
  const raw = useSyncExternalStore(subscribe, getSearch, () => '');
  return new URLSearchParams(raw);
}

export function navigate(to: string, options: { replace?: boolean } = {}): void {
  if (options.replace) window.history.replaceState(null, '', to);
  else window.history.pushState(null, '', to);
  window.scrollTo(0, 0);
  for (const l of listeners) l();
}

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'onClick'> & {
  to: string;
  children: ReactNode;
  onClick?: () => void;
};

export function Link({ to, children, onClick, ...rest }: LinkProps) {
  const handle = (e: MouseEvent<HTMLAnchorElement>): void => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    onClick?.();
    navigate(to);
  };
  return (
    <a href={to} onClick={handle} {...rest}>
      {children}
    </a>
  );
}
